/**
 * The built-in keyboard: when to raise it, and how its keys write text.
 *
 * WHY IT EXISTS (owner, 2026-09-22, late). On a projector with nothing but its
 * remote, no text box in the app can be typed into — not search, not the setup
 * URL, not a password. Measured on the ADT-3, same field, same session, with
 * the page's own event log:
 *
 *   air-mouse CLICK on an on-screen keyboard key   beforeinput + input  -> types
 *   air-mouse HARDWARE keyboard                    real keycodes        -> types
 *   remote OK on an on-screen keyboard key         keydown Enter        -> nothing
 *
 * Android offers a key to the input method first and to the app only if the
 * method declines it. The page receiving `Enter` therefore means the projector's
 * keyboard DECLINED the press that should select a key: it navigates with the
 * D-pad (the highlight viewers watch move) and commits nothing. That is the
 * device's own input method, so nothing in the page can repair it — the app has
 * to draw the keys instead. Which keyboard is installed is not ours to choose:
 * `InputMethodManager` offers only a picker, and writing
 * `Settings.Secure.DEFAULT_INPUT_METHOD` needs `WRITE_SECURE_SETTINGS`.
 *
 * THE SIGNAL, and why it is safe to act on. `keyboardOnDemand` opens a field on
 * Enter and calls `preventDefault()` — so the press that OPENS a field arrives
 * here already default-prevented, and any LATER Enter that reaches the page is a
 * press the platform keyboard refused. Four rails keep that from firing where it
 * should not, because the vast majority of viewers have no such problem and must
 * never meet this keyboard:
 *
 *   - ONLY IN THE APP (`runsOnDevice`). On the website, not at all: a browser
 *     user with a keyboard and no mouse produces the same key pattern by simply
 *     Tab-ing to a field and pressing Enter twice, and their keyboard is not
 *     broken. The site can still have it on purpose — `Settings → Text entry →
 *     Always` — but nothing automatic happens there.
 *   - a FINGER cannot touch the screen (`navigator.maxTouchPoints`), and no
 *     pointer press has been seen. Both, because each covers the other's blind
 *     spot: a touch device that somehow reports no pointer event is still
 *     caught by the finger count, and an air mouse on a non-touch device is
 *     caught by the pointer press. Measured on the two devices that matter —
 *     Galaxy A15 `maxTouchPoints: 5`, ADT-3 `0` — while `ontouchstart` is
 *     `true` on BOTH, so it is no signal at all. This is what keeps every phone
 *     and tablet in the APK out of it.
 *   - the field is still EMPTY. If characters have arrived since it opened, the
 *     platform keyboard is typing and there is nothing to replace.
 *   - once per field opening. One raised keyboard per open field, so a viewer
 *     who ignores it and presses OK again does not get a second one.
 *   - but once this device has refused ONE press, every later opening raises it
 *     on the press that opens the field (`platformKeyboardFailed` below). Before
 *     that, opening a field costs a press that appears to do nothing at all.
 *
 * NOTHING HERE RUNS PER FRAME. The listeners are one early return each on
 * `keydown`, `input`, `focusin`/`focusout` and `pointerdown`, so the cost on a
 * 1 GB box is a few comparisons per remote press, and the keyboard itself is not
 * in the DOM at all until something has actually gone wrong.
 */

import { runsOnDevice } from "../api/client.js";
import { takesTyping } from "./keyboardOnDemand.js";

type Field = HTMLInputElement | HTMLTextAreaElement;

/**
 * Has this device's own keyboard ever refused the remote's OK, this session?
 *
 * When it has, the question is settled: every field on this device costs one
 * press that does nothing, and the viewer has no way to know why. So the answer
 * is remembered, and later fields raise the built-in keyboard on the press that
 * OPENS them — what `onOpenedByKey` is for.
 *
 * Measured on the ADT-3 (2026-09-22/23), which is what made this necessary: after
 * Back closed the built-in keyboard, focus returned to the field and the input
 * rule re-armed it inert (`readOnly`, `inputmode: none`), so the next OK was an
 * opening press again — the field became editable, nothing appeared, and a SECOND
 * press was needed to bring the keyboard back. First-run setup is three fields
 * (server URL, username, password): three presses of nothing, in the one flow
 * where a viewer has no way to know the trick.
 *
 * A latch can only ever be set where the rails above already held — the app, no
 * finger, no pointer, an empty field, and a press the platform keyboard refused —
 * so no ordinary device can reach it. A reload forgets it, `mode: "never"` still
 * wins over it, and on the website it is never set at all (`runsOnDevice`).
 */
let platformKeyboardDeclined = false;

/** Whether this device's own keyboard has already refused a press (see above). */
export function platformKeyboardFailed(): boolean {
  return platformKeyboardDeclined;
}

/**
 * What the installed watch currently believes — for the debug server's `/state`.
 *
 * Every one of these was inferred the hard way on the projector (2026-09-23):
 * whether the viewer's field is open, whether anything has been typed since, and
 * whether a pointer has ever been seen decide whether the next OK will raise the
 * keyboard, and from the sofa there is no way to tell which one is blocking.
 */
export interface KeyboardWatchSnapshot {
  /** Is the field the viewer opened, if any (a label, never the element). */
  openField: EventTarget | null;
  typed: boolean;
  pointerSeen: boolean;
  deviceKeyboardFailed: boolean;
}

/**
 * The watch installed most recently, so the debug bridge can ask it (the app
 * installs exactly one; tests may install several, and the newest answers).
 */
let installed: (() => KeyboardWatchSnapshot) | null = null;

export function keyboardWatchSnapshot(): KeyboardWatchSnapshot | null {
  return installed ? installed() : null;
}

/** Not exported: a field is a field here, and callers only ever pass it back. */
function asTypingField(target: EventTarget | null): Field | null {
  const el = target as { tagName?: string; type?: string; disabled?: boolean } | null;
  if (!el || typeof el !== "object") return null;
  if (el.tagName !== "INPUT" && el.tagName !== "TEXTAREA") return null;
  if (el.disabled) return null;
  if (el.tagName === "INPUT" && !takesTyping(String(el.type ?? "text"))) return null;
  return target as unknown as Field;
}

/**
 * How many fingers this screen can be touched with (0 on a TV or a set-top
 * box, 5 on the phone measured above). `navigator` is optional in a worker or a
 * stripped environment, so this never assumes it.
 */
function hasTouch(): boolean {
  const navigator = globalThis.navigator as { maxTouchPoints?: number } | undefined;
  return (navigator?.maxTouchPoints ?? 0) > 0;
}

/**
 * Put the DEVICE's own keyboard away, when there is an app to ask.
 *
 * The other half of `keyboardOnDemand`'s request. On the projector the platform
 * keyboard is already on screen when ours appears — it comes up on the field's
 * own activation and simply refuses to commit — so the app's keyboard used to be
 * drawn on top of a keyboard that was up too (owner, 2026-09-22: "that keyboard
 * shouldn't show on the phone and both were up"). One keyboard at a time, and
 * the one that works is ours.
 *
 * Feature-detected like the show half: absent on the website and on any build
 * older than this one, where it is simply a no-op.
 */
export function hideDeviceKeyboard(): void {
  const device = (globalThis as { sliqueKeyboard?: { hide?: () => unknown } }).sliqueKeyboard;
  if (!device || typeof device.hide !== "function") return;
  try {
    device.hide();
  } catch {
    // Never worth breaking the keyboard that is opening over.
  }
}

export interface DeclinedOkWatchOptions {
  doc?: Document;
  /**
   * The platform keyboard refused the remote's OK on an open field — raise the
   * built-in one. Fires at most once per field opening.
   */
  onDeclinedOk: (field: Field) => void;
  /**
   * A field was opened by a key press, before anything has been typed. Only for
   * the "always" preference, where the viewer has asked for the built-in
   * keyboard rather than waiting for the platform one to fail.
   */
  onOpenedByKey?: (field: Field) => void;
}

/**
 * Watch for a field being opened, and for the platform keyboard declining the
 * press that should have typed into it. Returns a function that stops watching.
 */
export function installDeclinedOkWatch({
  doc = document,
  onDeclinedOk,
  onOpenedByKey,
}: DeclinedOkWatchOptions): () => void {
  // Has this device ever produced a pointer press? A remote never does, so a
  // device that has is one whose own keyboard we leave alone.
  let pointerSeen = false;
  /** The field the viewer opened, while it is still untouched. */
  let openField: Field | null = null;
  /** Has anything been typed since it opened? */
  let typed = false;
  /**
   * The field the viewer is in, and what it held when they came to it. Kept across the blur and
   * refocus of the SAME field that an on-screen keyboard being put away and shown again causes
   * (no other element takes the focus); dropped when focus really moves to another element.
   */
  let baseline: { field: Field; value: string } | null = null;

  const onPointerDown = (): void => {
    pointerSeen = true;
  };

  // A field that holds focus while INERT is not open (keyboardOnDemand), and one
  // the page re-focuses by itself is not an opening either; tracking the typed
  // state per field is all this needs.
  const onFocusIn = (event: Event): void => {
    const field = asTypingField(event.target);
    if (!field) return;
    openField = field.readOnly ? null : field;
    if (!baseline || baseline.field !== field) {
      baseline = { field, value: field.value };
      typed = false;
    }
  };

  const onFocusOut = (event: Event): void => {
    // Focus going to NOTHING (relatedTarget null) is the keyboard being put away or the window
    // losing focus: the field is still the one the viewer is in. Focus going to another
    // element is navigation, and a later return is a fresh question.
    if ((event as FocusEvent).relatedTarget) {
      openField = null;
      typed = false;
      baseline = null;
    }
  };

  const onInput = (event: Event): void => {
    const field = asTypingField(event.target);
    if (field && field === openField) typed = true;
  };

  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.key !== "Enter") return;
    const field = asTypingField(event.target);
    if (event.defaultPrevented) {
      // The press that OPENED the field: keyboardOnDemand consumed it, and the
      // field is editable from here (measured — the rule clears `readOnly` on
      // exactly this press). That is the window this keyboard belongs in.
      if (field && !field.readOnly && !pointerSeen && onOpenedByKey) {
        openField = field;
        typed = false;
        baseline = { field, value: field.value };
        // Already prevented by the rule; repeated deliberately, because this
        // press must never carry a default action into the new keyboard.
        event.preventDefault();
        onOpenedByKey(field);
        return;
      }
      if (field && !field.readOnly) {
        openField = field;
        typed = false;
        baseline = { field, value: field.value };
      }
      return;
    }
    // An Enter the page still received, on a field the viewer opened: the
    // platform keyboard refused it. Every rail has to hold for this to fire.
    if (!field || field !== openField || field.readOnly) return;
    if (typed || pointerSeen || hasTouch()) return;
    // The field changed since the viewer opened it: the platform keyboard IS typing. The
    // `typed` flag alone was not enough (owner, 2026-10-13, onn box: "the one we built for the
    // projector keyboard acting up"): a keyboard shown, put away with its own Done and shown
    // again blurs and refocuses the field, which reset the flag, so a search the device's own
    // keyboard had typed looked untouched and the next OK was taken for a refusal. That latched
    // "this device's keyboard is broken" and every field after opened ours. Comparing with the
    // value at the opening keeps the projector case: a PREFILLED field the keyboard refuses to
    // edit is still unchanged, so it is still a refusal.
    if (baseline && baseline.field === field && field.value !== baseline.value) return;
    // The website is left alone entirely (see the module comment): a browser
    // keyboard that is not broken can produce this exact key pattern.
    if (!runsOnDevice()) return;
    // From here the device's own keyboard is known to be unusable by a remote,
    // so later openings do not have to earn it press by press (see the latch).
    platformKeyboardDeclined = true;
    openField = null;
    // CONSUME THE PRESS THAT RAISES IT. Measured on the projector: without
    // this, the keyboard opened and the field read `qrd` where the viewer had
    // pressed r and d — because this keydown's default action still ran, and by
    // then the focus ring had moved onto the first key, so the browser pressed
    // it ("Enter activates the focused control"). The press that summons the
    // keyboard is spent on summoning it.
    event.preventDefault();
    onDeclinedOk(field);
  };

  // Blur and focusout both, like the input rule: a WebView is not guaranteed to
  // deliver the pair in any particular order, and a stale "open field" would
  // make a later press look like a failure that never happened.
  doc.addEventListener("pointerdown", onPointerDown, true);
  doc.addEventListener("focusin", onFocusIn, true);
  doc.addEventListener("focus", onFocusIn, true);
  doc.addEventListener("focusout", onFocusOut, true);
  doc.addEventListener("blur", onFocusOut, true);
  doc.addEventListener("input", onInput, true);
  doc.addEventListener("keydown", onKeyDown, true);

  const snapshot = (): KeyboardWatchSnapshot => ({
    openField,
    typed,
    pointerSeen,
    deviceKeyboardFailed: platformKeyboardDeclined,
  });
  installed = snapshot;

  return () => {
    if (installed === snapshot) installed = null;
    doc.removeEventListener("pointerdown", onPointerDown, true);
    doc.removeEventListener("focusin", onFocusIn, true);
    doc.removeEventListener("focus", onFocusIn, true);
    doc.removeEventListener("focusout", onFocusOut, true);
    doc.removeEventListener("blur", onFocusOut, true);
    doc.removeEventListener("input", onInput, true);
    doc.removeEventListener("keydown", onKeyDown, true);
  };
}

